import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { ProjectRelationView, ProjectView } from "@portal/schemas";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
} from "@portal/ui";
import { useState } from "react";
import { addProjectRelation, removeProjectRelation } from "../../api/client";
import { useInvalidate, useProjectRelations, useProjects } from "../../api/queries";
import { CardStatus } from "../../components/cards/CardStatus";
import { LabelWithHint } from "../../components/Hint";
import { HomeSection } from "../../components/HomeSection";
import { InlineError } from "../../components/InlineError";
import { CREATE_NEW } from "../../components/select-create";
import { CreateProjectDialog } from "./CreateProjectDialog";
import { useRowNavigation } from "../../components/rowClick";
import { ProjectStatusBadge } from "./ProjectStatusBadge";
import { Plus, X } from "lucide-react";

const TYPE_LABELS = {
  RELATED: "関連",
  PREDECESSOR: "先行 → 後継",
  DERIVED: "派生元 → 派生先",
} as const;

/** 見ている側から相手への言い方。 */
function describe(r: ProjectRelationView): string {
  switch (r.type) {
    case "RELATED":
      return "関連";
    case "PREDECESSOR":
      return r.forward ? "後継" : "先行";
    case "DERIVED":
      return r.forward ? "派生先" : "派生元";
  }
}

export function ProjectRelationsCard({ project }: { project: ProjectView }) {
  const rowNav = useRowNavigation();
  const relations = useProjectRelations(project.id);
  const invalidate = useInvalidate();
  const remove = useMutation({
    mutationFn: (relationId: string) => removeProjectRelation(project.id, relationId),
    onSuccess: () => invalidate.projects(),
  });
  return (
    <HomeSection
      data-testid="project-relations-card"
      title="関連プロジェクト"
      description="先行・後継、派生、その他の関連"
      action={project.canEdit && <AddRelationDialog project={project} />}
    >
      <CardStatus
        isPending={relations.isPending}
        isError={relations.isError}
        refetch={() => relations.refetch()}
      />
      {relations.data && relations.data.relations.length === 0 && (
        <p className="text-sm text-muted-foreground">関連するプロジェクトはありません。</p>
      )}
      <ul className="flex flex-col divide-y text-sm">
        {relations.data?.relations.map((r) => (
          <li
            key={r.id}
            className="flex cursor-pointer items-center justify-between gap-2 py-2 hover:bg-accent"
            {...rowNav(r.otherProjectId)}
          >
            <span className="flex items-center gap-2">
              <span className="w-14 text-xs text-muted-foreground">{describe(r)}</span>
              <Link
                to="/projects/$projectId"
                params={{ projectId: r.otherProjectId }}
                className="hover:underline"
              >
                {r.otherProjectName}
              </Link>
              <ProjectStatusBadge status={r.otherProjectStatus} />
            </span>
            {project.canEdit && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => remove.mutate(r.id)}
                disabled={remove.isPending}
                aria-label="解除"
                title="解除"
              >
                <X />
              </Button>
            )}
          </li>
        ))}
      </ul>
      <InlineError error={remove.error} />
    </HomeSection>
  );
}

function AddRelationDialog({ project }: { project: ProjectView }) {
  const [open, setOpen] = useState(false);
  const [toProjectId, setToProjectId] = useState("");
  const [type, setType] = useState<keyof typeof TYPE_LABELS>("RELATED");
  const [note, setNote] = useState("");
  // 「＋ 新しいプロジェクトを作成…」で開く作成のダイアログ
  const [creatingProject, setCreatingProject] = useState(false);
  const projects = useProjects();
  const invalidate = useInvalidate();
  const add = useMutation({
    mutationFn: () => addProjectRelation(project.id, { toProjectId, type, note }),
    onSuccess: async () => {
      await invalidate.projects();
      setOpen(false);
      setToProjectId("");
      setNote("");
    },
  });
  const candidates = (projects.data?.projects ?? []).filter((p) => p.id !== project.id);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus />
          追加
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>関連プロジェクトを追加</DialogTitle>
          <DialogDescription className="sr-only">
            関係と相手のプロジェクトを選びます。
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <LabelWithHint
              htmlFor="relation-type"
              hint="向きのある関係は「このプロジェクト → 相手」の向きで登録します。"
            >
              関係
            </LabelWithHint>
            <NativeSelect
              id="relation-type"
              value={type}
              onChange={(e) => setType(e.target.value as keyof typeof TYPE_LABELS)}
            >
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <NativeSelectOption key={k} value={k}>
                  {v}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="relation-target">相手のプロジェクト</Label>
            <NativeSelect
              id="relation-target"
              value={toProjectId}
              onChange={(e) =>
                e.target.value === CREATE_NEW
                  ? setCreatingProject(true)
                  : setToProjectId(e.target.value)
              }
            >
              <NativeSelectOption value="">選択…</NativeSelectOption>
              {candidates.map((p) => (
                <NativeSelectOption key={p.id} value={p.id}>
                  {p.name}
                </NativeSelectOption>
              ))}
              {/* 相手がまだ無ければ、ここから作ってそのまま選べる(ユーザー要望 2026-10-15) */}
              <NativeSelectOption value={CREATE_NEW}>
                ＋ 新しいプロジェクトを作成…
              </NativeSelectOption>
            </NativeSelect>
            <CreateProjectDialog
              open={creatingProject}
              onOpenChange={setCreatingProject}
              onCreated={(p) => setToProjectId(p.id)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="relation-note">備考</Label>
            <Input id="relation-note" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <InlineError error={add.error ?? projects.error} />
        </div>
        <DialogFooter>
          <Button onClick={() => add.mutate()} disabled={!toProjectId || add.isPending}>
            <Plus />
            追加
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
